import { environment } from "../../environments/environment";
import { PreOrderData } from "./product.types";

export interface DepositCopy {
    title: string;
    message: (deposit?: number, price?: number, remaining?: number) => string;
    additionalInfo: string;
    /**
     * Whether the amount carried by `preOrder.deposit` is a PARTIAL payment that
     * still leaves a balance to be charged later.
     *
     * False means that amount is not a deposit at all: either nothing is charged
     * (`none`) or it is the whole price, already paid (`full`). In both cases the
     * "Deposit" row must stay hidden, and the subscriptions page labels the amount
     * "Amount Paid" instead.
     */
    hasDeposit: boolean;
}

const noneCopy: DepositCopy = {
    title: 'No Deposit Required',
    message: (_deposit, price) =>
        `No deposit is required to reserve this item. ` +
        `You'll be charged the full <b>$${(price ?? 0).toFixed(2)}</b> per unit when your order is delivered &mdash; ` +
        `the charge is placed on our next payment cycle and typically appears on your card ` +
        `within <b>1&ndash;2 business days</b>.`,
    additionalInfo: 'No amount is charged now.',
    hasDeposit: false,
};

const feeCopy: DepositCopy = {
    title: 'Reservation Fee Deposit',
    message: () =>
        `Pre-orders require a deposit for each item. ` +
        `Deposits are refundable if canceled within <b>48 hours</b>. ` +
        `After the date in the product description, deposits are <b>non-refundable</b>. ` +
        `The deposit amount will be deducted from your order total on the week of delivery ` +
        `when the full item price is charged.`,
    additionalInfo: 'This deposit <b>will be deducted from your final payment</b> but will not be refunded '
        + 'if the order is canceled after the date in the product description.',
    hasDeposit: true,
};

const advanceCopy: DepositCopy = {
    title: 'Advanced Payment Deposit',
    message: () =>
        `Pre-orders require a deposit for each item. ` +
        `Deposits are refundable if canceled within <b>48 hours</b>. ` +
        `After the date in the product description, deposits are <b>non-refundable</b>. ` +
        `The deposit amount will be deducted from your order total on the week of delivery ` +
        `when the full item price is charged.`,
    additionalInfo: 'This deposit <b>will be deducted from your final payment</b> but will not be refunded '
        + 'if the order is canceled after the date in the product description.',
    hasDeposit: true,
};

const fullCopy: DepositCopy = {
    title: 'Full Payment Upfront',
    message: (_deposit, price) =>
        `Full payment of <b>$${(price ?? 0).toFixed(2)}</b> per unit is required now to reserve your item. ` +
        `It is billed on our next payment cycle (typically appears on your card within <b>1&ndash;2 business days</b>). ` +
        `<b>Nothing else will be charged</b> when your order is delivered.`,
    additionalInfo: 'You are paying in full now. '
        + 'This payment is non-refundable if you cancel after the pre-order deadline.',
    hasDeposit: false,
};

/**
 * Copy por modalidad de depósito.
 *
 * Claves v2 (`preOrder.depositKind`) y alias legacy (`preOrder.depositType`),
 * para que el mismo diccionario sirva con backends nuevos y viejos.
 */
const depositCopies: Record<string, DepositCopy> = {
    none: noneCopy,
    fee: feeCopy,
    advance: advanceCopy,
    full: fullCopy,

    fee_deposit: feeCopy,
    advance_deposit: advanceCopy,
    full_deposit: fullCopy,
};

/**
 * Resuelve el copy de una pre-orden. ÚNICO sitio que decide la modalidad:
 * prefiere el `depositKind` de la v2 y cae al `depositType` legacy.
 *
 * Usarlo en TODOS los consumidores; leer `depositType` a pelo deja de funcionar
 * en cuanto el backend deje de enviarlo.
 */
export function resolveDepositCopy(preOrder?: PreOrderData | null): DepositCopy | null {
    const key = resolveDepositKey(preOrder);
    return key ? (depositCopies[key] ?? null) : null;
}

/** La clave de modalidad ya resuelta, para condiciones propias de una marca. */
export function resolveDepositKey(preOrder?: PreOrderData | null): string | null {
    if (!preOrder) return null;
    return preOrder.depositKind ?? preOrder.depositType ?? null;
}

export const depositConfig = {
    ...depositCopies,

    preorderedProductsDisclaimer: `
        <p>Please note that pre-orders cannot be canceled through the website, and quantities can only be increased. Increasing the quantity may also raise the deposit amount (if applicable). Decreasing the quantity is not allowed. If you need assistance or have any questions, please contact us at <a class="fw-semibold" href="mailto:${environment.config.contactEmail}">${environment.config.contactEmail}</a>.</p>
        <p class="m-0"><strong>Pre-order products can only be modified until their respective deadlines. Please review the modification dates carefully.</strong></p>
    `,
    productAvailabilityDisclaimer: (endDate: string) => `This product is only available for pre-order until <b>${endDate}</b>`,
    increaseQuantityModal: {
        title: 'Confirm Quantity Increase',
        message: (product: { name: string, prevQuantity: number, newQuantity: number }) => `You are about to increase the quantity of <b>${product.name}</b> from <b>${product.prevQuantity}</b> to <b>${product.newQuantity}</b>. Please note that once this change is made, you will not be able to decrease the quantity. This update may also increase the required deposit for your pre-order. Do you want to proceed?`
    },
    deadlinePassedMessage: 'Update not allowed; the deadline has passed.'
}
